import { useState, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useServerFn } from '@tanstack/react-start';
import { motion } from 'motion/react';
import { usePersistentState } from '@/hooks/usePersistentState';
import {
  Phone, Mail, MapPin, CalendarClock, Loader2, CheckCircle2, Clock, Video, ChevronDown, Check,
} from 'lucide-react';
import PageGlow from '@/components/PageGlow';
import HeroWave from '@/components/HeroWave';
import { SERVICES } from '@/lib/services-catalog';
import { getPublicSlots, bookPublicCall } from '@/lib/public-booking.functions';

type Slot = { start: string; end: string; time: string };
type DaySlots = { date: string; label: string; slots: Slot[] };
type EnquiryType = 'enquire' | 'partner' | 'other';

const GRAD = '#5048E5';
const fg = 'hsl(var(--foreground))';
const fgMuted = 'hsl(var(--muted-foreground))';
const surface = 'hsl(var(--card))';
const border = 'hsl(var(--border))';
const HEAD = 'Outfit, sans-serif';
const FONT = 'Plus Jakarta Sans, sans-serif';

const ENQUIRY_OPTIONS: { value: EnquiryType; label: string }[] = [
  { value: 'enquire', label: 'Book a discovery call' },
  { value: 'partner', label: 'Partner with us' },
  { value: 'other', label: 'Other' },
];

const INPUT = 'w-full rounded-xl px-3.5 py-3 text-sm outline-none transition-all focus:ring-2';
const inputStyle = {
  background: 'hsl(var(--muted))',
  border: `1px solid ${border}`,
  color: fg,
  fontFamily: FONT,
} as const;

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35, ease: 'easeOut' as const } },
};
const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

export default function BookConsultationPage() {
  const navigate = useNavigate();
  const fetchSlots = useServerFn(getPublicSlots);
  const book = useServerFn(bookPublicCall);

  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState<DaySlots[]>([]);
  const [timezone, setTimezone] = useState('');

  const [activeDay, setActiveDay] = usePersistentState('enquiry-active-day', 0);
  const [selected, setSelected, clearSelected] = usePersistentState<Slot | null>('enquiry-selected', null);

  const [enquiryType, setEnquiryType, clearEnquiry] = usePersistentState<EnquiryType>('enquiry-type', 'enquire');
  const [name, setName, clearName] = usePersistentState('enquiry-name', '');
  const [email, setEmail, clearEmail] = usePersistentState('enquiry-email', '');
  const [phone, setPhone, clearPhone] = usePersistentState('enquiry-phone', '');
  const [note, setNote, clearNote] = usePersistentState('enquiry-note', '');
  const [otherReason, setOtherReason, clearOtherReason] = usePersistentState('enquiry-other-reason', '');
  const [selectedServices, setSelectedServices, clearSelectedServices] = usePersistentState<string[]>('enquiry-services', []);
  // Spam honeypot — must stay empty for real people; bots tend to fill every field.
  const [website, setWebsite] = useState('');

  const [booking, setBooking] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetchSlots();
        if (!alive) return;
        setDays(res?.configured ? res.days : []);
        setTimezone(res?.configured ? res.timezone : '');
      } catch {
        if (alive) setDays([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const day = days[activeDay];
  const hasSlots = days.length > 0;

  async function confirm() {
    if (!selected) { setError('Please pick a time.'); return; }
    if (!name.trim()) { setError('Please enter your name.'); return; }
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) { setError('Please enter a valid email.'); return; }
    if (enquiryType !== 'other' && !phone.trim()) { setError('Please enter your phone number.'); return; }
    if (enquiryType === 'other' && !otherReason.trim()) { setError('Please describe the reason for your call.'); return; }
    setBooking(true);
    setError('');

    const serviceLine = enquiryType === 'enquire' && selectedServices.length
      ? `Services: ${selectedServices.join(', ')}`
      : '';
    const reasonLine = enquiryType === 'other' && otherReason.trim()
      ? `Reason for call: ${otherReason.trim()}`
      : '';
    const noteBody = note.trim();
    const finalNote = [serviceLine, reasonLine, noteBody].filter(Boolean).join('\n\n') || undefined;

    try {
      const res = await book({
        data: {
          start: selected.start,
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim() || undefined,
          note: finalNote,
          enquiryType,
          origin: window.location.origin,
          website,
        },
      });
      if (res?.ok) {
        try {
          sessionStorage.setItem(
            'db_call_booking',
            JSON.stringify({
              reference: res.reference,
              when: `${day?.label} at ${selected.time}`,
              timezone: res.timezone || timezone,
              name: name.trim(),
              email: email.trim(),
            }),
          );
        } catch { /* ignore */ }
        clearSelected(); clearEnquiry(); clearName(); clearEmail(); clearPhone(); clearNote(); clearOtherReason(); clearSelectedServices();
        navigate({ to: '/call-booked' });
        return;
      } else if (res?.reason === 'taken') {
        setError('That slot was just taken — please pick another.');
        const fresh = await fetchSlots();
        setDays(fresh?.configured ? fresh.days : []);
        setSelected(null);
      } else {
        setError('Could not book that slot. Please try again.');
      }
    } catch {
      setError('Could not book that slot. Please try again.');
    } finally {
      setBooking(false);
    }
  }

  return (
    <section className="relative overflow-hidden py-6 pb-20 md:py-10 md:pb-24" style={{ background: 'hsl(var(--background))' }}>
      <HeroWave fadeBottom={58} bleed="both" />
      <PageGlow />

      <div className="relative mx-auto max-w-6xl px-6">
        {/* ── Page header ── */}
        <motion.div initial="hidden" animate="visible" variants={stagger} className="mb-8 max-w-2xl">
          <motion.h1 variants={fadeUp} className="text-3xl font-bold md:text-4xl"
            style={{ fontFamily: HEAD, lineHeight: 1.1, color: fg }}>
            <span style={{ background: `linear-gradient(90deg, ${GRAD} 0%, hsl(14 100% 57%) 100%)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
              Book a call with us
            </span>
          </motion.h1>
          <motion.p variants={fadeUp} className="mt-3 text-base leading-relaxed md:text-lg"
            style={{ fontFamily: FONT, color: fgMuted }}>
            {{
              enquire: 'Pick a time for a friendly, no-obligation chat about your project or business goals.',
              partner: 'Pick a time for a friendly, no-obligation conversation about partnering with us.',
              other: 'Pick a time for a friendly, no-obligation chat — tell us what you need below.',
            }[enquiryType]}
          </motion.p>
        </motion.div>

        {/* ── Two-column layout ── */}
        <motion.div initial="hidden" animate="visible" variants={stagger} className="grid grid-cols-1 gap-6 lg:grid-cols-5">

          {/* ── Booking (left, wider) ── */}
          <motion.div variants={fadeUp} className="lg:col-span-3">
            <div className="rounded-2xl p-6 md:p-8" style={{ background: surface, border: `1px solid ${border}` }}>
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 size={26} className="animate-spin" style={{ color: GRAD }} />
                </div>
              ) : !hasSlots ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <CalendarClock size={40} style={{ color: GRAD }} className="mb-4" />
                  <p className="max-w-sm text-sm" style={{ color: fgMuted, fontFamily: FONT }}>
                    No online slots are available right now. Please email{' '}
                    <a href="mailto:hello@digitallybaffled.com" className="font-bold underline" style={{ color: GRAD }}>
                      hello@digitallybaffled.com
                    </a>{' '}
                    and we&apos;ll arrange a time.
                  </p>
                </div>
              ) : (
                <div className="flex flex-col gap-6">
                  {/* Enquiry type */}
                  <div>
                    <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider" style={{ color: fgMuted, fontFamily: HEAD }}>
                      What&apos;s this about?
                    </label>
                    <div className="relative">
                      <select
                        value={enquiryType}
                        onChange={(e) => setEnquiryType(e.target.value as EnquiryType)}
                        className={`${INPUT} appearance-none pr-10`}
                        style={inputStyle}>
                        {ENQUIRY_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                      <ChevronDown size={16} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2" style={{ color: fgMuted }} />
                    </div>
                    {enquiryType === 'other' && (
                      <textarea
                        value={otherReason}
                        onChange={(e) => setOtherReason(e.target.value)}
                        rows={3}
                        maxLength={500}
                        placeholder="Please briefly describe the reason for your call"
                        className={`${INPUT} resize-none mt-3`}
                        style={inputStyle}
                      />
                    )}
                    {enquiryType === 'enquire' && (
                      <div className="mt-3">
                        <label className="mb-2 block text-xs font-bold uppercase tracking-wider" style={{ color: fgMuted, fontFamily: HEAD }}>
                          How can we help? <span className="font-medium normal-case opacity-70">(select all that apply)</span>
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {SERVICES.map((s) => {
                            const active = selectedServices.includes(s.title);
                            return (
                              <button
                                key={s.slug}
                                type="button"
                                onClick={() => {
                                  setSelectedServices((prev) =>
                                    active ? prev.filter((t) => t !== s.title) : [...prev, s.title]
                                  );
                                }}
                                className="flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition-all"
                                style={active
                                  ? { background: GRAD, color: '#ffffff' }
                                  : { background: 'hsl(var(--muted))', color: fg, border: `1px solid ${border}` }}>
                                {active && <Check size={14} />}
                                {s.title}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Day selector */}
                  <div>
                    <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider" style={{ color: fgMuted, fontFamily: HEAD }}>
                      Pick a day
                    </label>
                    <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
                      {days.map((d, i) => (
                        <button key={d.date} type="button"
                          onClick={() => { setActiveDay(i); setSelected(null); }}
                          className="shrink-0 rounded-xl px-3.5 py-2.5 text-xs font-bold transition-all"
                          style={i === activeDay
                            ? { background: GRAD, color: '#ffffff' }
                            : { background: 'hsl(var(--muted))', color: fg, border: `1px solid ${border}` }}>
                          {d.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Time grid */}
                  <div>
                    <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider" style={{ color: fgMuted, fontFamily: HEAD }}>
                      Pick a time
                    </label>
                    {day?.slots.length ? (
                      <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                        {day.slots.map((s) => {
                          const sel = selected?.start === s.start;
                          return (
                            <button key={s.start} type="button"
                              onClick={() => { setSelected(s); setError(''); }}
                              className="rounded-lg px-2 py-2.5 text-sm font-semibold transition-all"
                              style={sel
                                ? { background: GRAD, color: '#ffffff' }
                                : { background: 'hsl(var(--muted))', color: fg, border: `1px solid ${border}` }}>
                              {s.time}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm" style={{ color: fgMuted, fontFamily: FONT }}>No times left on this day — try another.</p>
                    )}
                    {timezone && (
                      <p className="mt-2 text-xs" style={{ color: fgMuted, fontFamily: FONT }}>
                        Times shown in {timezone.replace(/_/g, ' ')}
                      </p>
                    )}
                  </div>

                  {/* Contact details */}
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {/* Honeypot: hidden from people, catches spam bots. Never remove. */}
                    <input
                      type="text"
                      name="website"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      value={website}
                      onChange={(e) => setWebsite(e.target.value)}
                      style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
                    />
                    <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name *"
                      className={INPUT} style={inputStyle} maxLength={120} />
                    <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email address *" type="email"
                      className={INPUT} style={inputStyle} maxLength={200} />
                    <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={enquiryType === 'other' ? 'Phone (optional)' : 'Phone *'} type="tel"
                      className={`${INPUT} sm:col-span-2`} style={inputStyle} maxLength={40} required={enquiryType !== 'other'} />
                    <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={500}
                      placeholder={
                        enquiryType === 'partner'
                          ? "Tell us about your company and how you'd like to partner with us..."
                          : enquiryType === 'other'
                            ? 'Anything else we should know? (optional)'
                            : "Tell us about your business and what you're trying to achieve..."
                      }
                      className={`${INPUT} resize-none sm:col-span-2`} style={inputStyle} />
                  </div>

                  {error && (
                    <p className="flex items-center gap-1.5 text-sm font-semibold" style={{ color: 'hsl(0 72% 55%)', fontFamily: FONT }}>
                      {error}
                    </p>
                  )}

                  <button
                    type="button"
                    onClick={confirm}
                    disabled={booking || !selected}
                    className="flex items-center justify-center gap-2 rounded-xl px-4 py-3.5 text-sm font-bold text-white transition-all hover:opacity-90 disabled:opacity-50"
                    style={{ background: GRAD, fontFamily: FONT }}>
                    {booking ? <Loader2 size={16} className="animate-spin" /> : selected ? <CheckCircle2 size={16} /> : <CalendarClock size={16} />}
                    {booking ? 'Booking…' : selected ? `Book ${day?.label}, ${selected.time}` : 'Select a time'}
                  </button>
                </div>
              )}
            </div>
          </motion.div>

          {/* ── Sidebar (right) ── */}
          <motion.div variants={fadeUp} className="flex flex-col gap-4 lg:col-span-2">

            {/* What to expect */}
            <div className="rounded-2xl p-6" style={{ background: GRAD }}>
              <h3 className="mb-4 text-lg font-bold" style={{ fontFamily: HEAD, color: '#ffffff' }}>What to expect</h3>
              <ul className="flex flex-col gap-3">
                {[
                  { Icon: Clock, text: '30-minute call, zero pressure' },
                  { Icon: Video, text: 'Online — link sent to your email' },
                ].map(({ Icon, text }) => (
                  <li key={text} className="flex items-center gap-3 text-sm font-medium" style={{ color: 'rgba(255,255,255,0.9)', fontFamily: FONT }}>
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg" style={{ background: 'rgba(255,255,255,0.18)' }}>
                      <Icon size={15} className="text-white" />
                    </span>
                    {text}
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact details */}
            <div className="rounded-2xl p-6" style={{ background: surface, border: `1px solid ${border}` }}>
              <h3 className="mb-4 text-sm font-bold uppercase tracking-wider" style={{ fontFamily: HEAD, color: fgMuted }}>Contact details</h3>
              <div className="flex flex-col gap-3">
                {[
                  { Icon: Phone, value: '07511 683542', href: 'tel:+447511683542', sub: 'Available 24/7' },
                  { Icon: Mail, value: 'hello@digitallybaffled.com', href: 'mailto:hello@digitallybaffled.com', sub: 'Reply within 24h' },
                  { Icon: MapPin, value: 'United Kingdom', href: undefined, sub: 'Remote-first team' },
                ].map(({ Icon, value, sub, href }) => (
                  <div key={value} className="flex items-center gap-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg" style={{ background: 'hsla(243,75%,59%,0.12)' }}>
                      <Icon size={14} style={{ color: GRAD }} />
                    </div>
                    <div>
                      {href ? (
                        <a href={href} className="text-sm font-semibold transition-opacity hover:opacity-70"
                          style={{ color: fg, fontFamily: FONT }}>{value}</a>
                      ) : (
                        <div className="text-sm font-semibold" style={{ color: fg, fontFamily: FONT }}>{value}</div>
                      )}
                      <div className="text-xs" style={{ color: fgMuted }}>{sub}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Already a client */}
            <div className="rounded-2xl px-6 py-4" style={{ background: surface, border: `1px solid ${border}` }}>
              <p className="text-sm" style={{ color: fgMuted, fontFamily: FONT }}>
                Already a client?{' '}
                <a href="/login" className="font-semibold transition-opacity hover:opacity-70" style={{ color: GRAD }}>
                  Go to your portal →
                </a>
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
